import {ChangeDetectionStrategy, Component, computed, input, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {UmpireComponentGallery} from '@tisa-group/models';
import {ImageVariantModule} from '@tisa-group/core';
import {SvgIconComponent} from '@tisa-group/client-ui/components/svg-icon';

@Component({
  selector: 'tisa-gallery',
  imports: [CommonModule, ImageVariantModule, SvgIconComponent],
  templateUrl: './gallery.component.html',
  styleUrl: './gallery.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    tabindex: '0',
    '(keydown)': 'onKeyDown($event)',
    '(touchstart)': 'onTouchStart($event)',
    '(touchend)': 'onTouchEnd($event)',
  },
})
export class GalleryComponent {
  readonly component = input<UmpireComponentGallery | undefined>();

  readonly currentImageIndex = signal<number>(0);

  private touchStartX = 0;

  readonly images = computed(() => {
    const component = this.component();
    if (component) {
      return component.images;
    }
    return [];
  });

  readonly currentImage = computed(() => {
    const images = this.images();
    const index = this.currentImageIndex();
    return images[index] || null;
  });

  readonly totalImages = computed(() => this.images().length);

  goToImage(index: number): void {
    const total = this.totalImages();
    if (total > 0 && index >= 0 && index < total) {
      this.currentImageIndex.set(index);
    }
  }

  nextImage(): void {
    const current = this.currentImageIndex();
    const total = this.totalImages();
    this.goToImage((current + 1) % total);
  }

  previousImage(): void {
    const current = this.currentImageIndex();
    const total = this.totalImages();
    this.goToImage(current === 0 ? total - 1 : current - 1);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.totalImages() <= 1) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.previousImage();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.nextImage();
        break;
    }
  }

  onTouchStart(event: TouchEvent): void {
    if (this.totalImages() <= 1) return;
    this.touchStartX = event.touches[0].clientX;
  }

  onTouchEnd(event: TouchEvent): void {
    if (this.totalImages() <= 1 || this.touchStartX === 0) return;

    const touchEndX = event.changedTouches[0].clientX;
    const deltaX = this.touchStartX - touchEndX;
    const minSwipeDistance = 50;

    if (Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0) {
        this.nextImage();
      } else {
        this.previousImage();
      }
    }

    this.touchStartX = 0;
  }
}
